import type {
  Tenant,
  Campus,
  TenantSubscription,
  SubscriptionHistory,
  TenantSetting,
} from "../types/tenant.types";

// ── Helpers ───────────────────────────────────────────────────────────────────

function paginate<T>(items: T[], page = 1, pageSize = 20) {
  const start = (page - 1) * pageSize;
  const data = items.slice(start, start + pageSize);
  return {
    data,
    meta: {
      pagination: {
        page,
        page_size: pageSize,
        total_items: items.length,
        total_pages: Math.ceil(items.length / pageSize),
      },
    },
  };
}

// ── Tenant Profile ────────────────────────────────────────────────────────────

export const MOCK_TENANT: Tenant = {
  id: "019ccc6c-ca7d-72aa-a984-11ac996d160c",
  name: "Sunrise Schools Group",
  slug: "sunrise-schools",
  school_type: "mixed",
  curriculum_system: "kenya_cbc",
  term_structure: "three_terms",
  country: "KE",
  timezone: "Africa/Nairobi",
  default_language: "en",
  currency: "KES",
  phone: "+254 700 123 456",
  email: "info@sunriseschools.ac.ke",
  website: "https://www.sunriseschools.ac.ke",
  address_line1: "12 Mwangi Road",
  address_line2: "Westlands",
  city: "Nairobi",
  state_province: "Nairobi County",
  postal_code: "00100",
  logo_url: undefined,
  motto: "Excellence Through Knowledge",
  established_date: "1998-01-01",
  registration_number: "MOE/SCH/1998/042",
  subscription_tier: "standard",
  max_students: 1000,
  campus_count: 3,
  total_student_count: 742,
  status: "active",
  version: 4,
  created_at: "2024-01-15T08:00:00Z",
  updated_at: "2025-11-20T14:30:00Z",
};

export function getMockTenant(): Tenant {
  return { ...MOCK_TENANT };
}

// ── Campuses ──────────────────────────────────────────────────────────────────

export const MOCK_CAMPUSES: Campus[] = [
  {
    id: "campus-001",
    tenant_id: MOCK_TENANT.id,
    name: "Nairobi Main Campus",
    code: "NBI-MAIN",
    school_type: "mixed",
    curriculum_system: "kenya_cbc",
    term_structure: "three_terms",
    status: "active",
    phone: "+254 700 111 001",
    email: "nairobi@sunriseschools.ac.ke",
    website: "https://nairobi.sunriseschools.ac.ke",
    address_line1: "12 Mwangi Road, Westlands",
    city: "Nairobi",
    state_province: "Nairobi County",
    postal_code: "00100",
    country: "KE",
    motto: "Strive to Achieve",
    principal_name: "Dr. Alice Kamau",
    established_date: "1998-01-01",
    registration_number: "MOE/SCH/1998/042",
    student_count: 487,
    staff_count: 38,
    timezone: "Africa/Nairobi",
    default_language: "en",
    currency: "KES",
    is_primary: true,
    version: 7,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-10-05T09:15:00Z",
  },
  {
    id: "campus-002",
    tenant_id: MOCK_TENANT.id,
    name: "Mombasa Branch",
    code: "MSA-BRANCH",
    school_type: "primary",
    curriculum_system: "kenya_cbc",
    term_structure: "three_terms",
    status: "active",
    phone: "+254 700 111 002",
    email: "mombasa@sunriseschools.ac.ke",
    address_line1: "45 Nyali Road",
    city: "Mombasa",
    state_province: "Mombasa County",
    postal_code: "80100",
    country: "KE",
    principal_name: "Mr. Hassan Mwangi",
    established_date: "2005-01-01",
    registration_number: "MOE/SCH/2005/118",
    student_count: 213,
    staff_count: 17,
    timezone: "Africa/Nairobi",
    default_language: "sw",
    currency: "KES",
    is_primary: false,
    version: 3,
    created_at: "2024-02-01T08:00:00Z",
    updated_at: "2025-09-12T11:00:00Z",
  },
  {
    id: "campus-003",
    tenant_id: MOCK_TENANT.id,
    name: "Kisumu Campus",
    code: "KSM-CAMPUS",
    school_type: "secondary",
    curriculum_system: "kenya_cbc",
    term_structure: "three_terms",
    status: "inactive",
    phone: "+254 700 111 003",
    email: "kisumu@sunriseschools.ac.ke",
    address_line1: "8 Oginga Odinga Street",
    city: "Kisumu",
    state_province: "Kisumu County",
    postal_code: "40100",
    country: "KE",
    principal_name: "Ms. Grace Otieno",
    established_date: "2012-01-01",
    registration_number: "MOE/SCH/2012/203",
    student_count: 42,
    staff_count: 6,
    timezone: "Africa/Nairobi",
    default_language: "en",
    currency: "KES",
    is_primary: false,
    version: 2,
    created_at: "2024-03-10T08:00:00Z",
    updated_at: "2025-06-01T10:00:00Z",
  },
];

export function getMockCampuses(params?: { page?: number; page_size?: number; search?: string; status?: string }) {
  let items = [...MOCK_CAMPUSES];
  if (params?.search) {
    const q = params.search.toLowerCase();
    items = items.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }
  if (params?.status) {
    items = items.filter((c) => c.status === params.status);
  }
  return paginate(items, params?.page ?? 1, params?.page_size ?? 50);
}

export function getMockCampus(id: string): Campus | undefined {
  return MOCK_CAMPUSES.find((c) => c.id === id);
}

// ── Subscription ──────────────────────────────────────────────────────────────

export const MOCK_SUBSCRIPTION: TenantSubscription = {
  subscription_tier: "standard",
  max_students: 1000,
  status: "active",
};

export function getMockSubscription(): TenantSubscription {
  return { ...MOCK_SUBSCRIPTION };
}

export const MOCK_SUBSCRIPTION_HISTORY: SubscriptionHistory[] = [
  {
    id: "hist-001",
    tenant_id: MOCK_TENANT.id,
    changed_by: "admin-001",
    old_tier: "basic",
    new_tier: "standard",
    old_max_students: 300,
    new_max_students: 1000,
    reason: "School enrollment grew beyond 300 students",
    effective_at: "2025-01-15T10:00:00Z",
    created_at: "2025-01-15T10:00:00Z",
  },
  {
    id: "hist-002",
    tenant_id: MOCK_TENANT.id,
    changed_by: "admin-001",
    old_tier: "free",
    new_tier: "basic",
    old_max_students: 50,
    new_max_students: 300,
    reason: "End of trial period — moved to Basic plan",
    effective_at: "2024-04-01T08:00:00Z",
    created_at: "2024-04-01T08:00:00Z",
  },
  {
    id: "hist-003",
    tenant_id: MOCK_TENANT.id,
    changed_by: "system",
    old_tier: "free",
    new_tier: "free",
    old_max_students: 0,
    new_max_students: 50,
    reason: "Account created",
    effective_at: "2024-01-15T08:00:00Z",
    created_at: "2024-01-15T08:00:00Z",
  },
];

export function getMockSubscriptionHistory(params?: { page?: number; page_size?: number }) {
  return paginate(MOCK_SUBSCRIPTION_HISTORY, params?.page ?? 1, params?.page_size ?? 10);
}

// ── Settings ──────────────────────────────────────────────────────────────────

export const MOCK_SETTINGS: TenantSetting[] = [
  {
    id: "setting-001",
    tenant_id: MOCK_TENANT.id,
    key: "attendance.late_threshold_minutes",
    value: 15,
    description: "Minutes after class start time before a student is marked late",
    is_public: false,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-08-01T09:00:00Z",
  },
  {
    id: "setting-002",
    tenant_id: MOCK_TENANT.id,
    key: "fee.payment_reminder_days",
    value: 7,
    description: "Days before fee due date to send payment reminder SMS/email",
    is_public: false,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-08-01T09:00:00Z",
  },
  {
    id: "setting-003",
    tenant_id: MOCK_TENANT.id,
    key: "fee.penalty_pct",
    value: 5,
    description: "Percentage penalty applied to overdue fee balances per term",
    is_public: false,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-09-10T11:00:00Z",
  },
  {
    id: "setting-004",
    tenant_id: MOCK_TENANT.id,
    key: "portal.allow_parent_grade_view",
    value: true,
    description: "Allow parents to view grade reports in the parent portal",
    is_public: true,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-09-10T11:00:00Z",
  },
  {
    id: "setting-005",
    tenant_id: MOCK_TENANT.id,
    key: "portal.allow_parent_attendance_view",
    value: true,
    description: "Allow parents to view their child's attendance in the parent portal",
    is_public: true,
    created_at: "2024-01-15T08:00:00Z",
    updated_at: "2025-09-10T11:00:00Z",
  },
  {
    id: "setting-006",
    tenant_id: MOCK_TENANT.id,
    key: "sms.sender_id",
    value: "SUNRISE",
    description: "SMS sender ID shown on outbound messages (max 11 characters)",
    is_public: false,
    created_at: "2024-02-01T08:00:00Z",
    updated_at: "2025-10-01T08:00:00Z",
  },
  {
    id: "setting-007",
    tenant_id: MOCK_TENANT.id,
    key: "academic.grading_pass_mark",
    value: 50,
    description: "Minimum percentage score required to pass a subject",
    is_public: false,
    created_at: "2024-02-01T08:00:00Z",
    updated_at: "2025-10-01T08:00:00Z",
  },
];

export function getMockSettings(params?: { page?: number; page_size?: number }) {
  return paginate(MOCK_SETTINGS, params?.page ?? 1, params?.page_size ?? 100);
}
